/**
 * Image utility functions for the frontend
 */

/**
 * Adds cache-busting parameters to an image URL
 * @param url - The image URL to add cache-busting to
 * @param timestamp - Optional timestamp to use (defaults to current time)
 * @param forceCacheBust - Optional flag to force cache-busting even for logo/fallback images
 * @returns The URL with cache-busting parameters
 */
export const addCacheBuster = (url: string, timestamp?: number, forceCacheBust: boolean = false): string => {
  if (!url || typeof url !== 'string') {
    return url;
  }

  // Don't add cache buster to fallback images or data URLs (unless forced)
  if (!forceCacheBust && (
      url.includes('/images/logo1.jpg') || 
      url.includes('/images/fallback.svg') || 
      url.startsWith('data:') ||
      url.startsWith('blob:')
    )) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';
  const cacheBuster = timestamp || Date.now();
  
  return `${url}${separator}v=${cacheBuster}`;
};

/**
 * Extracts the S3 key from a proxy URL
 * @param url - The proxy URL (e.g., /api/images/proxy/listings/123/image.jpg)
 * @returns The S3 key or null if not a proxy URL
 */
export const extractS3KeyFromUrl = (url: string): string | null => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const match = url.match(/\/api\/images\/proxy\/(.+)$/);
  return match ? match[1] : null;
};

/**
 * Normalizes an image URL to ensure it's a proxy URL if it's an S3 URL
 * @param url - The image URL to normalize
 * @returns The normalized URL
 */
export const normalizeImageUrl = (url: string): string => {
  if (!url || typeof url !== 'string') {
    return '/images/logo1.jpg';
  }

  // If it's already a proxy URL, return as is
  if (url.includes('/api/images/proxy/')) {
    return url;
  }

  // If it's an S3 URL, convert to proxy URL
  if (url.includes('amazonaws.com')) {
    const urlParts = url.split('.amazonaws.com/');
    if (urlParts.length > 1) {
      return `/api/images/proxy/${urlParts[1]}`;
    }
  }

  // If it's a relative URL starting with /, return as is
  if (url.startsWith('/')) {
    return url;
  }

  // If it's a full URL (http/https), return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Default fallback
  return '/images/logo1.jpg';
};

/**
 * Gets the primary image URL from a listing object
 * @param listing - The listing object
 * @returns The primary image URL
 */
export const getPrimaryImageUrl = (listing: any): string => {
  if (!listing) {
    return '/images/logo1.jpg';
  }

  // Try responsive_images first (most reliable)
  if (listing.responsive_images && Array.isArray(listing.responsive_images) && listing.responsive_images.length > 0) {
    return normalizeImageUrl(listing.responsive_images[0].url);
  }

  // Try image_urls array
  if (listing.image_urls && Array.isArray(listing.image_urls) && listing.image_urls.length > 0) {
    return normalizeImageUrl(listing.image_urls[0]);
  }

  // Try single image_url
  if (listing.image_url) {
    if (Array.isArray(listing.image_url) && listing.image_url.length > 0) {
      return normalizeImageUrl(listing.image_url[0]);
    }
    if (typeof listing.image_url === 'string') {
      return normalizeImageUrl(listing.image_url);
    }
  }

  return '/images/logo1.jpg';
};

/**
 * Gets all image URLs from a listing object
 * @param listing - The listing object
 * @returns Array of image URLs
 */
export const getAllImageUrls = (listing: any): string[] => {
  if (!listing) {
    return ['/images/logo1.jpg'];
  }

  const urls: string[] = [];

  // Try responsive_images first
  if (listing.responsive_images && Array.isArray(listing.responsive_images)) {
    urls.push(...listing.responsive_images.map((img: any) => normalizeImageUrl(img.url)));
  }

  // If no responsive_images, try image_urls
  if (urls.length === 0 && listing.image_urls) {
    if (Array.isArray(listing.image_urls)) {
      urls.push(...listing.image_urls.map(normalizeImageUrl));
    } else if (typeof listing.image_urls === 'string') {
      urls.push(normalizeImageUrl(listing.image_urls));
    }
  }

  // If still no URLs, try single image_url
  if (urls.length === 0 && listing.image_url) {
    if (Array.isArray(listing.image_url)) {
      urls.push(...listing.image_url.map(normalizeImageUrl));
    } else if (typeof listing.image_url === 'string') {
      urls.push(normalizeImageUrl(listing.image_url));
    }
  }

  return urls.length > 0 ? urls : ['/images/logo1.jpg'];
}; 